<template>
  <v-row>
    <v-col cols="12">
      <v-card class="card-container" elevation="0">
        <v-data-table
          :headers="headers"
          :items="products"
          :item-key="'id'"
          :style="tableVars"
          :height="tableHeight"
          fixed-header
          hide-default-footer
        >
          <template v-slot:[`item.image`]="{ item }">
            <div class="product-image-wrapper">
              <v-img
                v-if="getProductImage(item)"
                :src="getProductImage(item)"
                :alt="item.name || 'Product'"
                class="product-image"
                contain
              />
              <div v-else class="product-image-placeholder">
                <v-icon color="#9ca3af">mdi-image-outline</v-icon>
              </div>
            </div>
          </template>

          <template v-slot:[`item.name`]="{ item }">
            <div class="product-name">{{ item.name || "-" }}</div>
            <div v-if="item.sku" class="product-sku">SKU: {{ item.sku }}</div>
          </template>

          <template v-slot:[`item.price`]="{ item }">
            {{ formatPrice(item.price) }}
          </template>

          <template v-slot:[`item.stock`]="{ item }">
            <span :class="getStockClass(item)">
              {{ item.stock !== undefined ? item.stock : "-" }}
            </span>
          </template>

          <template v-slot:[`item.status`]="{ item }">
            <v-chip
              small
              :color="getStatus(item).color"
              :text-color="getStatus(item).text"
              class="status-chip"
            >
              <v-icon v-if="getStatus(item).icon" left size="18">
                {{ getStatus(item).icon }}
              </v-icon>
              {{ getStatus(item).label }}
            </v-chip>
          </template>

          <template v-slot:[`item.createdAt`]="{ item }">
            {{ formatDate(item.createdAt) }}
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <div class="d-flex align-center">
              <v-btn
                color="#1F2A39"
                text
                class="d-flex align-center action-btn"
                @click="$emit('edit', item)"
              >
                <div class="detail-btn mr-2">
                  <v-icon size="20" color="#F4811F">mdi-pencil-outline</v-icon>
                </div>
                <span>แก้ไข</span>
              </v-btn>



              <v-btn
                color="#1F2A39"
                text
                class="d-flex align-center action-btn ml-2"
                @click="$emit('delete', item)"
              >
                <div class="detail-btn mr-2">
                  <v-icon size="20" color="#EE2526">mdi-delete-outline</v-icon>
                </div>
                <span>ลบ</span>
              </v-btn>
            </div>
          </template>
        </v-data-table>
      </v-card>

    </v-col>
  </v-row>
</template>

<script>
export default {
  name: "ProductTable",

  props: {
    products: { type: Array, required: true },
  },

  data() {
    return {};
  },

  computed: {
    tableHeight() {
      return this.products.length < 7 ? "auto" : "380px";
    },

    tableVars() {
      return {
        "--actions-col": "320px",
      };
    },

    headers() {
      return [
        {
          text: "รูปภาพ",
          value: "image",
          align: "left",
          class: "header-image",
          cellClass: "cell-image",
          sortable: false,
          width: "100px",
        },
        {
          text: "ชื่อสินค้า",
          value: "name",
          align: "left",
          class: "header-name",
          cellClass: "cell-normal",
          sortable: false,
          width: "250px",
        },
        {
          text: "ราคา",
          value: "price",
          align: "right",
          class: "header-price",
          cellClass: "cell-normal",
          sortable: false,
          width: "150px",
        },
        {
          text: "สต็อก",
          value: "stock",
          align: "right",
          class: "header-stock",
          cellClass: "cell-normal",
          sortable: false,
          width: "120px",
        },
        {
          text: "สถานะ",
          value: "status",
          align: "left",
          class: "header-status",
          cellClass: "cell-status",
          sortable: false,
          width: "140px",
        },
        {
          text: "วันที่สร้าง",
          value: "createdAt",
          align: "left",
          class: "header-created",
          cellClass: "cell-normal",
          sortable: false,
          width: "200px",
        },
        {
          text: "จัดการ",
          value: "actions",
          align: "left",
          class: "header-actions",
          cellClass: "cell-actions",
          sortable: false,
          width: "320px",
        },
      ];
    },
  },

  watch: {
    products() {
      this.scrollToTopOfTable();
    },
  },

  methods: {
    getProductImage(item) {
      const image =
        item.image ||
        item.imageUrl ||
        item.image_url ||
        item.thumbnail ||
        item.images?.[0] ||
        "";
      if (!image) return "";

      if (/^https?:\/\//i.test(image) || image.startsWith("blob:")) {
        return image;
      }

      const base = process.env.VUE_APP_API_URL || "";
      if (!base) return image;

      const cleanBase = base.replace(/\/+$/, "");
      const cleanPath = image.startsWith("/") ? image : `/${image}`;
      return `${cleanBase}${cleanPath}`;
    },

    formatPrice(price) {
      if (price === undefined || price === null) return "-";
      return `฿${Number(price).toLocaleString("th-TH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    },

    getStockClass(item) {
      const stock = item.stock;
      if (stock === undefined || stock === null) return "stock-normal";
      if (stock === 0) return "stock-out";
      if (stock < 10) return "stock-low";
      return "stock-normal";
    },

    getStatus(item) {
      const status = (item.status || "").toLowerCase();

      if (status === "active" || status === "published") {
        return {
          label: "เปิดใช้งาน",
          color: "#E1FAE8",
          text: "#0FAE63",
          icon: "mdi-check-circle-outline",
          isActive: true,
        };
      }

      if (status === "inactive" || status === "draft") {
        return {
          label: "แบบร่าง",
          color: "#F3F4F6",
          text: "#6B717F",
          icon: "mdi-file-document-outline",
          isActive: false,
        };
      }

      return {
        label: "ปิดใช้งาน",
        color: "#FDECEC",
        text: "#E11D48",
        icon: "mdi-close-circle-outline",
        isActive: false,
      };
    },

    formatDate(d) {
      if (!d) return "-";

      const date = new Date(d);
      if (isNaN(date.getTime())) return "-";

      const monthsFullThai = [
        "มกราคม",
        "กุมภาพันธ์",
        "มีนาคม",
        "เมษายน",
        "พฤษภาคม",
        "มิถุนายน",
        "กรกฎาคม",
        "สิงหาคม",
        "กันยายน",
        "ตุลาคม",
        "พฤศจิกายน",
        "ธันวาคม",
      ];

      const day = date.getDate();
      const month = monthsFullThai[date.getMonth()];
      const year = date.getFullYear() + 543;

      return `${day} ${month} ${year}`;
    },

    scrollToTopOfTable() {
      this.$nextTick(() => {
        const wrapper = this.$el.querySelector(".v-data-table__wrapper");
        if (wrapper) {
          wrapper.scrollTo({ top: 0 });
        } else {
          window.scrollTo({ top: 0 });
        }
      });
    },
  },
};
</script>

<style scoped>
.card-container {
  overflow: hidden !important;
  border-radius: 12px !important;
  box-shadow: 0px 0px 2px 0px #1018280f, 0px 0px 3px 0px #1018281a !important;
}

::v-deep(.cell-normal),
::v-deep(.cell-actions) {
  font-size: 14px !important;
  font-weight: 400 !important;
  color: #1f2a39 !important;
}

::v-deep(.header-image),
::v-deep(.header-name),
::v-deep(.header-price),
::v-deep(.header-stock),
::v-deep(.header-status),
::v-deep(.header-created),
::v-deep(.header-actions) {
  font-size: 16px !important;
  font-weight: 600 !important;
  color: #6b717f !important;
  background-color: #f9fafc !important;
  box-shadow: none !important;
}

.product-image-wrapper {
  width: 60px;
  height: 60px;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
}

.product-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.product-image-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f3f4f6;
}

.product-name {
  font-size: 14px;
  font-weight: 500;
  color: #1f2a39;
  margin-bottom: 4px;
}

.product-sku {
  font-size: 12px;
  color: #6b717f;
}

.stock-normal {
  color: #0fae63;
  font-weight: 500;
}

.stock-low {
  color: #f59e0b;
  font-weight: 500;
}

.stock-out {
  color: #e11d48;
  font-weight: 500;
}

.status-chip {
  border-radius: 8px !important;
}

.count-text {
  font-size: 14px;
  font-weight: 400;
  color: #394253;
}

.items-per-page-text {
  font-size: 14px;
  font-weight: 400;
  color: #394253;
}

.custom-select {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 6px 8px;
  cursor: pointer;
}

.caret-icon {
  width: 16px;
  height: 16px;
  transition: transform 0.3s;
}

.custom-select.menu-open .caret-icon {
  transform: rotate(180deg);
}

::v-deep(.cell-actions) {
  position: sticky;
  right: 0;
  background: #fff;
}

::v-deep(.header-actions) {
  position: sticky;
  right: 0;
  background: #f9fafc;
  box-shadow: none !important;
}

::v-deep(.cell-status) {
  position: sticky;
  right: var(--actions-col);
  background: #fff;
  z-index: 1;
  box-shadow: -2px 0 3px -1px #1018280f !important;
}

::v-deep(.header-status) {
  position: sticky;
  right: var(--actions-col);
  z-index: 3;
  background: #f9fafc;
  box-shadow: -2px 0 3px -1px #1018280f !important;
}

::v-deep .v-pagination__item,
::v-deep .v-pagination__navigation {
  font-size: 14px !important;
  font-weight: 400 !important;
  border-radius: 8px !important;
  width: 34px !important;
  height: 34px !important;
  box-shadow: 0px 0px 2px 0px #1018280f, 0px 0px 3px 0px #1018281a !important;
}

::v-deep(.v-pagination .v-pagination__navigation .v-icon) {
  font-size: 12px !important;
  font-weight: 400 !important;
  color: #6b717f !important;
  font-family: "Noto Sans Thai", sans-serif !important;
  font-style: normal !important;
}

::v-deep(.v-pagination .v-pagination__navigation) {
  width: 65px !important;
  height: 34px !important;
}

.action-btn {
  min-width: 0 !important;
  padding: 0 4px !important;
  font-size: 14px !important;
}

.detail-btn {
  border-radius: 4px !important;
  padding: 2px !important;
  box-shadow: 0px 0px 2px 0px #1018280f, 0px 0px 3px 0px #1018281a !important;
  width: 24px !important;
  height: 24px !important;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>

